import { meeting } from '@microsoft/teams-js';
import { hasMetaOsHubContext } from '@ms/yammer-web-support/dist/metaOsHub';
import { rejectAfter } from '@ms/yammer-web-support/dist/promises/timeout';
import { GetMeetingDetailsTimedoutError } from 'domains/teams/types';
import type { MeetingDetails } from 'state/teams';
import { getMeetingTenantIdFromQueryParam } from 'teams/queryParams';

type GetMeetingDetails = () => Promise<MeetingDetails>;
export const getMeetingDetails: GetMeetingDetails = async () => {
  const hasMetaOsContext = await hasMetaOsHubContext();

  return new Promise((resolve, reject) => {
    if (!hasMetaOsContext) {
      reject(new Error('Failed to get meeting details: not loaded inside Teams.'));

      return;
    }

    meeting.getMeetingDetails((error, meetingDetails) => {
      if (error != null) {
        const sdkErrorWithErrorCode = JSON.stringify(error);
        reject(new Error(`Unable to get meeting details, throws SdkError ${sdkErrorWithErrorCode}`));
      }

      if (meetingDetails == null) {
        reject(new Error('Unable to get meeting details'));
      }

      resolve({
        meetingUrl: meetingDetails?.details.joinUrl,
        title: (meetingDetails?.details as meeting.IMeetingDetails).title,
        meetingType: meetingDetails?.details.type,
        organizerId: meetingDetails?.organizer.id,
        meetingTenantId: meetingDetails?.organizer.tenantId,
      });
    });
  });
};

const MEETING_DETAILS_TIMEOUT = 10000;

type GetMeetingDetailsWithTimeout = () => Promise<MeetingDetails>;
export const getMeetingDetailsWithTimeout: GetMeetingDetailsWithTimeout = () => {
  const getMeetingDetailsPromise = getMeetingDetails();
  const timeoutPromise = rejectAfter(MEETING_DETAILS_TIMEOUT, new GetMeetingDetailsTimedoutError());

  return Promise.race([getMeetingDetailsPromise, timeoutPromise]);
};

type ResolveMeetingTenantId = (url: string) => Promise<string | undefined>;
export const resolveMeetingTenantId: ResolveMeetingTenantId = async (url) => {
  const meetingTenantIdFromQueryParam = getMeetingTenantIdFromQueryParam(url);
  if (meetingTenantIdFromQueryParam) {
    return meetingTenantIdFromQueryParam;
  }

  const meetingDetails = await getMeetingDetailsWithTimeout();

  return meetingDetails.meetingTenantId;
};
